import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type { Magazine, RangeSessionMagazine } from '../types';

/** A packed-mag line joined to its group (name/caliber/capacity for the card). */
export interface PackedMagLine extends RangeSessionMagazine {
  magazines?: Pick<Magazine, 'id' | 'label' | 'brand' | 'model' | 'caliber' | 'capacity' | 'download_to' | 'quantity_owned'>;
}

const LINE_SELECT =
  '*, magazines(id, label, brand, model, caliber, capacity, download_to, quantity_owned)';

/** The mag groups packed for a trip, group-joined, oldest-added first. */
export async function loadSessionMagazines(
  sessionId: string,
): Promise<{ data: PackedMagLine[]; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('range_session_magazines')
    .select(LINE_SELECT)
    .eq('range_session_id', sessionId)
    .order('created_at', { ascending: true });
  return { data: (data as PackedMagLine[]) ?? [], error };
}

/** Pack a mag group onto a trip (dupes accumulate is prevented by UNIQUE). */
export interface PackOptions {
  /**
   * M77c — the user's CONFIRMED count of rounds in the group at pack time, and
   * whether it came from the one-tap suggestion. A PAIR: the DB refuses one
   * without the other, because a number that might be a guess is unrenderable
   * (the UI could not tell a hand count from a machine suggestion).
   *
   * Omit both for pack-and-immediately-fire (ShotSheet) and for rePack, where a
   * snapshot is meaningless or would be a stale guess wearing a count's clothes.
   */
  roundsAtPack?: number | null;
  estimated?: boolean;
  /** M77c — optional "Feeds" attribution. Build is never defaulted (D3). */
  firearmId?: string | null;
  buildId?: string | null;
}

export async function packMagazine(
  sessionId: string,
  magazineId: string,
  magsTaken: number,
  opts?: PackOptions,
): Promise<{ error: PostgrestError | null }> {
  const atPack = opts?.roundsAtPack ?? null;
  const { error } = await supabase.from('range_session_magazines').insert({
    range_session_id: sessionId,
    magazine_id: magazineId,
    mags_taken: Math.max(0, Math.trunc(magsTaken)),
    // Written as a pair or not at all — see PackOptions.
    rounds_at_pack: atPack,
    rounds_at_pack_estimated: atPack === null ? null : !!opts?.estimated,
    firearm_id: opts?.firearmId ?? null,
    build_id: opts?.buildId ?? null,
  });
  return { error };
}

/** M77c — set the "Feeds" attribution on an OPEN packed line. */
export async function setMagLineAttribution(
  lineId: string,
  firearmId: string | null,
  buildId: string | null,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_session_magazines')
    .update({ firearm_id: firearmId, build_id: buildId })
    .eq('id', lineId);
  return { error };
}

/** M77c — edit the pack-time snapshot while the line is still open. Typing a
 *  number always clears the estimated flag: a hand count is not a suggestion. */
export async function setRoundsAtPack(
  lineId: string,
  roundsAtPack: number | null,
  estimated: boolean,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('range_session_magazines')
    .update({
      rounds_at_pack: roundsAtPack,
      rounds_at_pack_estimated: roundsAtPack === null ? null : estimated,
    })
    .eq('id', lineId);
  return { error };
}

/** Remove a packed-mag line. Caller must revert it first if reconciled (the UI
 *  only offers remove on open lines; reconciled lines show Undo). */
export async function removePackedMag(lineId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.from('range_session_magazines').delete().eq('id', lineId);
  return { error };
}

/** Close out one packed-mag line: FIFO-deduct `roundsFired` from the group's
 *  loaded contents + write the ledger rows (server, atomic). One-shot.
 *  M70c (E7): `amend: true` re-opens an already-reconciled line and re-applies
 *  the new total in ONE server transaction. `p_amend` is only sent when
 *  amending, so the shipped close-out call shape stays byte-identical. */
export async function reconcileSessionMagazine(
  lineId: string,
  roundsFired: number,
  opts?: { amend?: boolean; magsReturnedLoaded?: number | null; roundsReturnedLoaded?: number | null },
): Promise<{ error: PostgrestError | null }> {
  const args: Record<string, unknown> = { p_line_id: lineId, p_rounds_fired: roundsFired };
  if (opts?.amend) args.p_amend = true;
  // M77c — only sent when the user actually answered. The RPC COALESCEs an
  // omitted value back to its pre-revert snapshot (OV2), so a fired-only amend
  // preserves returns instead of erasing them, and the shipped close-out call
  // shape stays byte-identical when nothing was recorded.
  if (opts?.magsReturnedLoaded != null) args.p_mags_returned_loaded = opts.magsReturnedLoaded;
  if (opts?.roundsReturnedLoaded != null) args.p_rounds_returned_loaded = opts.roundsReturnedLoaded;
  const { error } = await supabase.rpc('reconcile_session_magazine', args);
  return { error };
}

/**
 * M77c (A1) — record what came back on an ALREADY closed-out line.
 *
 * Deliberately NOT `reconcileSessionMagazine(..., {amend:true})`: that path
 * reverts and re-FIFOs against CURRENT magazine contents, so answering "what
 * came back" after the done-state's own "Load for next trip" would re-book the
 * trip's rounds against whatever lot is loaded now. This writes the three
 * return columns and nothing else.
 */
export async function updateMagLineReturns(
  lineId: string,
  magsReturnedLoaded: number | null,
  roundsReturnedLoaded: number | null,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('update_mag_line_returns', {
    p_line_id: lineId,
    p_mags_returned_loaded: magsReturnedLoaded,
    p_rounds_returned_loaded: roundsReturnedLoaded,
  });
  return { error };
}

/** Undo a mag close-out: restore quantity + contents, drop the ledger rows. */
export async function revertSessionMagazine(lineId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('revert_session_magazine', { p_line_id: lineId });
  return { error };
}
